import React, {useState, useEffect, useMemo} from 'react';
import {Bar} from 'react-chartjs-2';
import type {ChartData, ChartOptions} from 'chart.js';
import ArrCheckbox from '../arr-checkbox/ArrCheckbox';
import './ArrBarChartSelectable.scss';

export interface BarChartSelectableSeries {
  label: string;
  data: number[];
  color?: string;
}

export interface ArrBarChartSelectableProps {
  labels: string[];
  series: BarChartSelectableSeries[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  title?: string;
  horizontal?: boolean;
  selectedBars?: string[];
  onBarClick?: (
    barKey: string,
    barData: {label: string; value: number}
  ) => void;
  onSelectionChange?: (selectedKeys: string[]) => void;
  multiSelect?: boolean;
  showLegend?: boolean;
  showSelectAllCheckbox?: boolean;
  dataTestId: string;
}

const useCssVar = (name: string, fallback: string) =>
  useMemo(
    () =>
      getComputedStyle(document.documentElement)
        .getPropertyValue(name)
        .trim() || fallback,
    [name, fallback]
  );

const ArrBarChartSelectable: React.FC<ArrBarChartSelectableProps> = ({
  labels,
  series,
  xAxisLabel,
  yAxisLabel,
  title,
  horizontal = false,
  selectedBars = [],
  onBarClick,
  onSelectionChange,
  multiSelect = true,
  showLegend = false,
  showSelectAllCheckbox = false,
  dataTestId,
}) => {
  const [internalSelectedBars, setInternalSelectedBars] =
    useState<string[]>(selectedBars);

  useEffect(() => {
    setInternalSelectedBars(selectedBars);
  }, [selectedBars]);

  useEffect(() => {
    onSelectionChange?.(internalSelectedBars);
  }, [internalSelectedBars, onSelectionChange]);

  const highlightGrey = useCssVar('--highlight-grey', '#687E9F');
  const dataData05 = useCssVar('--data-data-05', '#00D0DB');
  const textBodyStrong = useCssVar('--text-body-strong', '#ccd5e3');
  const textBorderStrong = useCssVar('--text-border-string', '#8C9EBA');

  const getBarKey = (seriesIdx: number, dataIdx: number) =>
    `${seriesIdx}-${dataIdx}`;
  const allBarKeys = useMemo(() => {
    return series.flatMap((s, seriesIdx) =>
      s.data.map((_, dataIdx) => getBarKey(seriesIdx, dataIdx))
    );
  }, [series]);

  const allSelected = internalSelectedBars.length === allBarKeys.length;
  const isIndeterminate =
    internalSelectedBars.length > 0 &&
    internalSelectedBars.length < allBarKeys.length;

  const datasets = series.map((s, seriesIdx) => ({
    label: s.label,
    data: s.data,
    backgroundColor: s.data.map((_, dataIdx) =>
      internalSelectedBars.includes(getBarKey(seriesIdx, dataIdx))
        ? dataData05
        : highlightGrey
    ),
    borderWidth: 0,
    barThickness: 'flex',
    maxBarThickness: 200,
    minBarLength: 16,
    categoryPercentage: 0.8,
    barPercentage: 0.8,
  }));

  const chartData: ChartData<'bar'> = {labels, datasets};

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        display: showLegend,
        labels: {color: textBodyStrong},
      },
      title: title
        ? {
            display: true,
            text: title,
            color: textBodyStrong,
          }
        : undefined,
    },
    scales: {
      x: {
        title: {display: !!xAxisLabel, text: xAxisLabel, color: textBodyStrong},
        ticks: {color: textBodyStrong, precision: 0},
        grid: {
          display: true,
          drawTicks: false,
          lineWidth: 0.5,
          color: textBorderStrong,
          borderDash: [2, 2],
        } as any,
      },
      y: {
        title: {display: !!yAxisLabel, text: yAxisLabel, color: textBodyStrong},
        ticks: {color: textBodyStrong, autoSkip: false},
        grid: {display: true},
      },
    },
    onClick: (event, elements) => {
      if (!elements.length) return;
      const el = elements[0];
      const seriesIdx = el.datasetIndex;
      const dataIdx = el.index;
      const key = getBarKey(seriesIdx, dataIdx);

      const updated = multiSelect
        ? internalSelectedBars.includes(key)
          ? internalSelectedBars.filter((k) => k !== key)
          : [...internalSelectedBars, key]
        : internalSelectedBars.includes(key)
          ? []
          : [key];

      setInternalSelectedBars(updated);
      onBarClick?.(key, {
        label: labels[dataIdx],
        value: series[seriesIdx].data[dataIdx],
      });
    },
  };

  return (
    <div
      className="arr-bar-chart-selectable"
      data-testid={dataTestId || 'arr-bar-chart-container'}
    >
      {showSelectAllCheckbox && (
        <div
          className="arr-bar-chart-selectable__header"
          data-testid={`arr-bar-chart-selectable-select-all`}
        >
          <span
            className="arr-bar-chart-selectable__label"
            data-testid={`arr-bar-chart-selectable-select-all-text`}
          >
            Select All
          </span>
          <ArrCheckbox
            checked={allSelected}
            isIndeterminate={isIndeterminate}
            isAllSelected={allSelected}
            onChange={(checked) => {
              setInternalSelectedBars(checked ? allBarKeys : []);
            }}
            size="medium"
            aria-label="Select-all-bar-checkboxes"
          />
        </div>
      )}
      <div
        className="arr-bar-chart-selectable__chart"
        style={{height: '100%'}}
        data-testid={`arr-bar-chart-selectable-div`}
      >
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default ArrBarChartSelectable;
